// src/DevStreamFilters.js
import React, { useState } from "react";
import "./DevStream.css";

/**
 * DevStreamFilters - Filter inputs for dev streams
 *
 * Features:
 * - 4 filter fields: deviceId, actionId, eventType (dropdown), customSearch
 * - Negative search support: prefix with '-' to exclude (e.g., '-unlock')
 * - AND logic: all non-empty filters must match
 * - Collapsible section
 * - Real-time filtering (via hook)
 */
const DevStreamFilters = ({ filters, updateFilter }) => {
  const [collapsed, setCollapsed] = useState(false);

  const eventTypeOptions = [
    { value: "", label: "All Event Types" },
    { value: "SubscriptionConfirmation", label: "SubscriptionConfirmation" },
    { value: "DEVICE_STATE_UPDATED", label: "DEVICE_STATE_UPDATED" },
    { value: "DEVICE_ACTION_CREATED", label: "DEVICE_ACTION_CREATED" },
    { value: "DEVICE_ACTION_UPDATED", label: "DEVICE_ACTION_UPDATED" },
    {
      value: "DEVICE_NOTIFICATION_REPORTED",
      label: "DEVICE_NOTIFICATION_REPORTED",
    },
    {
      value: "DEVICE_BULK_DATA_STATE_UPDATED",
      label: "DEVICE_BULK_DATA_STATE_UPDATED",
    },
  ];

  const filterFields = [
    {
      key: "deviceId",
      label: "Device ID",
      placeholder: "Filter by device ID...",
      type: "text",
    },
    {
      key: "actionId",
      label: "Action ID",
      placeholder: "Filter by action ID...",
      type: "text",
    },
    {
      key: "eventType",
      label: "Event Type",
      type: "select",
      options: eventTypeOptions,
    },
    {
      key: "customSearch",
      label: "Custom Search",
      placeholder: "Search entire event JSON...",
      type: "text",
    },
  ];

  const hasActiveFilters = Object.values(filters).some(
    (value) => value.trim() !== ""
  );

  return (
    <div className="dev-stream-filters">
      <div className="filters-header" onClick={() => setCollapsed(!collapsed)}>
        <h3>
          Filters{" "}
          {hasActiveFilters && <span className="active-filters-badge">●</span>}
        </h3>
        <span className="collapse-icon">{collapsed ? "▶" : "▼"}</span>
      </div>

      {!collapsed && (
        <div className="filters-content">
          <div className="filter-hint">
            💡 Use <code>-term</code> to exclude matches (e.g.,{" "}
            <code>-unlock</code>)
          </div>

          <div className="filter-grid">
            {filterFields.map(({ key, label, placeholder, type, options }) => (
              <div key={key} className="filter-field">
                <label>{label}</label>
                {type === "select" ? (
                  <select
                    value={filters[key]}
                    onChange={(e) => updateFilter(key, e.target.value)}
                    className={filters[key].trim() ? "has-value" : ""}
                  >
                    {options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={filters[key]}
                    onChange={(e) => updateFilter(key, e.target.value)}
                    placeholder={placeholder}
                    className={filters[key].trim() ? "has-value" : ""}
                  />
                )}
              </div>
            ))}
          </div>

          {hasActiveFilters && (
            <button
              className="clear-filters-btn"
              onClick={() => {
                filterFields.forEach(({ key }) => updateFilter(key, ""));
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DevStreamFilters;
